/**
 * API Service for communicating with the backend
 */

const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  } catch (err) {
    console.error('Request failed:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

// Project types
interface Project {
  id: string;
  name: string;
  clientName?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Drawing types
interface Drawing {
  id: string;
  projectId: string;
  drawingNumber: string;
  type: string;
  status: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface SvgResponse {
  pageCount: number;
  pages: string[];
}

interface PdfResponse {
  pdfPath: string;
  pageCount: number;
}

// Project API
export const projectsApi = {
  list: () => request<Project[]>('/projects'),

  get: (id: string) => request<Project>(`/projects/${id}`),

  create: (data: { name: string; clientName?: string }) =>
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  update: (id: string, data: Partial<Project>) =>
    request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  delete: (id: string) =>
    request<void>(`/projects/${id}`, {
      method: 'DELETE'
    }),

  listDrawings: (projectId: string) =>
    request<Drawing[]>(`/projects/${projectId}/drawings`),

  createDrawing: (projectId: string, data: Omit<Drawing, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) =>
    request<Drawing>(`/projects/${projectId}/drawings`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
};

// Drawing API
export const drawingsApi = {
  get: (id: string) => request<Drawing>(`/drawings/${id}`),

  update: (id: string, data: Partial<Drawing>) =>
    request<Drawing>(`/drawings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  delete: (id: string) =>
    request<void>(`/drawings/${id}`, {
      method: 'DELETE'
    }),

  getSvg: (id: string) => request<SvgResponse>(`/drawings/${id}/svg`),

  getSvgPage: async (id: string, page: number): Promise<string> => {
    const response = await fetch(`${API_BASE}/drawings/${id}/svg?page=${page}`);
    return response.text();
  },

  generatePdf: (id: string) =>
    request<PdfResponse>(`/drawings/${id}/pdf`, {
      method: 'POST'
    }),

  getPdfUrl: (id: string) => `${API_BASE}/drawings/${id}/pdf`
};

// Health check
export const healthApi = {
  check: () => request<{ status: string; princeXML: string }>('/v1/health')
};
